/**
 * Gerät koppeln — der Netflix-Flow im Leitstand (P6 Block B, Review S1).
 *
 * Die Autorität ist die angemeldete Sitzung: Diese Seite scannt mit der
 * Kamera den QR-Code, den das neue Terminal zeigt (oder nimmt die 8-Zeichen-
 * Kurzform vom Terminal-Bildschirm entgegen), fragt Typ/Name/Standort ab und
 * genehmigt. Der Kopplungs-Code existiert nie als kopierbarer Link — das
 * Terminal holt sich sein Cookie danach selbst. Die Genehmigung verlangt
 * serverseitig Admin + Zwei-Faktor.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import jsQR from 'jsqr';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

type Schritt = 'code' | 'einrichten' | 'fertig';

/** Aus Scan/Eingabe den Code ziehen — akzeptiert puren Token, Kurzform und Alt-URLs. */
function extrahiereCode(roh: string): string | null {
  const text = roh.trim();
  const urlMatch = text.match(/[?&]token=([0-9a-fA-F]{64})/);
  if (urlMatch) return urlMatch[1];
  if (/^[0-9a-fA-F]{64}$/.test(text)) return text;
  if (/^[0-9a-fA-F]{8}$/.test(text)) return text.toLowerCase();
  return null;
}

export default function GeraetKoppeln() {
  const { user } = useAuth();

  const [schritt, setSchritt] = useState<Schritt>('code');
  const [code, setCode] = useState('');
  const [kurzform, setKurzform] = useState('');
  const [kameraFehler, setKameraFehler] = useState('');
  const [kameraLaeuft, setKameraLaeuft] = useState(false);

  const [typ, setTyp] = useState<'WSA' | 'VORARBEITER'>('WSA');
  const [name, setName] = useState('');
  const [standort, setStandort] = useState('');
  const [sende, setSende] = useState(false);
  const [fehler, setFehler] = useState('');
  const [stationId, setStationId] = useState('');

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanRef = useRef<number | null>(null);

  const stoppeKamera = useCallback(() => {
    if (scanRef.current) cancelAnimationFrame(scanRef.current);
    scanRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setKameraLaeuft(false);
  }, []);

  const uebernehmeCode = useCallback(
    (gefunden: string) => {
      setCode(gefunden);
      setSchritt('einrichten');
      stoppeKamera();
    },
    [stoppeKamera],
  );

  const starteKamera = useCallback(async () => {
    setKameraFehler('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      setKameraLaeuft(true);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const tick = () => {
        if (!streamRef.current || !ctx || !video.videoWidth) {
          scanRef.current = requestAnimationFrame(tick);
          return;
        }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        const bild = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qr = jsQR(bild.data, bild.width, bild.height);
        const gefunden = qr && extrahiereCode(qr.data);
        if (gefunden) {
          uebernehmeCode(gefunden);
          return;
        }
        scanRef.current = requestAnimationFrame(tick);
      };
      scanRef.current = requestAnimationFrame(tick);
    } catch {
      setKameraFehler(
        'Keine Kamera verfügbar — unten die Kurzform vom Terminal-Bildschirm eintippen.',
      );
    }
  }, [uebernehmeCode]);

  useEffect(() => () => stoppeKamera(), [stoppeKamera]);

  const kurzformUebernehmen = () => {
    const gefunden = extrahiereCode(kurzform);
    if (!gefunden) {
      setKameraFehler(
        'Die Kurzform hat 8 Zeichen (Buchstaben A–F und Ziffern) — steht groß auf dem Terminal.',
      );
      return;
    }
    uebernehmeCode(gefunden);
  };

  const genehmige = async () => {
    setSende(true);
    setFehler('');
    try {
      const res = await fetch('/api/admin/stations/pair', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token: code,
          type: typ,
          name: name.trim() || undefined,
          location: standort.trim() || undefined,
        }),
      });
      const antwort = await res.json().catch(() => null);
      if (!res.ok) {
        const codes: Record<string, string> = {
          PAIRING_NOT_FOUND:
            'Code unbekannt oder abgelaufen — am Terminal neu starten und erneut scannen.',
          PAIRING_EXPIRED: 'Der Code ist abgelaufen — am Terminal neu starten.',
          PAIRING_ALREADY_CLAIMED: 'Dieser Code wurde bereits eingelöst.',
          PAIRING_AMBIGUOUS: 'Die Kurzform ist gerade nicht eindeutig — bitte den QR-Code scannen.',
          TWO_FACTOR_REQUIRED:
            'Für das Koppeln ist die Zwei-Faktor-Anmeldung nötig — bitte neu anmelden.',
          TWO_FACTOR_SETUP_REQUIRED:
            'Für das Koppeln zuerst die Zwei-Faktor-Anmeldung einrichten (Profil → Zwei-Faktor).',
          RATE_LIMITED: 'Zu viele Versuche — bitte kurz warten.',
        };
        throw new Error(codes[antwort?.code] || antwort?.error || 'Koppeln fehlgeschlagen');
      }
      setStationId(antwort?.stationId ?? '');
      setSchritt('fertig');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Koppeln fehlgeschlagen');
    } finally {
      setSende(false);
    }
  };

  return (
    <LeitstandRahmen titel="Gerät koppeln" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Katalog
        </Link>

        {schritt === 'code' && (
          <>
            <Karte titel="Code vom Terminal scannen" marke="das neue Gerät zeigt ihn unter /pair">
              <div className="flex flex-col items-center gap-4">
                {!kameraLaeuft && (
                  <button onClick={() => void starteKamera()} className={KNOPF_PRIMAER}>
                    Kamera starten
                  </button>
                )}
                <video
                  ref={videoRef}
                  className={`w-full max-w-md rounded-leitstand border border-linie ${kameraLaeuft ? '' : 'hidden'}`}
                  muted
                  playsInline
                />
                {kameraLaeuft && (
                  <p className="text-[13px] text-tinte-3">
                    QR-Code des Terminals ins Bild halten …
                  </p>
                )}
              </div>
            </Karte>

            <Karte titel="Ohne Kamera" marke="Kurzform vom Terminal-Bildschirm">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={kurzform}
                  onChange={(e) => setKurzform(e.target.value)}
                  placeholder="z. B. A1B2C3D4"
                  maxLength={8}
                  className={`${EINGABE} w-44 font-zahl uppercase tracking-widest`}
                />
                <button onClick={kurzformUebernehmen} className={KNOPF_LEISE}>
                  Weiter
                </button>
              </div>
              {kameraFehler && <p className="mt-2 text-[13px] text-stand">{kameraFehler}</p>}
            </Karte>
          </>
        )}

        {schritt === 'einrichten' && (
          <Karte
            titel="Gerät einrichten"
            marke={`Code ${code.slice(0, 8).toUpperCase()} — mit dem Terminal abgleichen`}
          >
            <div className="flex flex-col gap-4">
              <p className="text-sm text-tinte-2">
                Die Kurzform oben muss mit der Anzeige auf dem Terminal übereinstimmen — dann
                richtest du genau das Gerät ein, das vor dir steht.
              </p>
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                <label className="flex flex-col gap-1.5">
                  <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                    Stationstyp *
                  </span>
                  <select
                    value={typ}
                    onChange={(e) => setTyp(e.target.value as 'WSA' | 'VORARBEITER')}
                    className={EINGABE}
                  >
                    <option value="WSA">WSA-Station (Waschschleuderautomaten)</option>
                    <option value="VORARBEITER">Vorarbeiter-Station</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                    Name
                  </span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="z. B. WSA Halle"
                    className={EINGABE}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                    Standort
                  </span>
                  <input
                    value={standort}
                    onChange={(e) => setStandort(e.target.value)}
                    placeholder="z. B. kleines Waschhaus"
                    className={EINGABE}
                  />
                </label>
              </div>
              {fehler && <p className="text-[13px] text-stoer">{fehler}</p>}
              <div className="flex flex-wrap gap-2">
                <button onClick={() => void genehmige()} disabled={sende} className={KNOPF_PRIMAER}>
                  Gerät koppeln
                </button>
                <button
                  onClick={() => {
                    setSchritt('code');
                    setFehler('');
                    setKurzform('');
                  }}
                  disabled={sende}
                  className={KNOPF_LEISE}
                >
                  Zurück
                </button>
              </div>
            </div>
          </Karte>
        )}

        {schritt === 'fertig' && (
          <Karte titel="Gekoppelt" marke="das Terminal verbindet sich jetzt selbst">
            <div className="flex flex-col gap-3">
              <p className="flex items-center gap-2 text-sm">
                <Zustand art="gut">fertig</Zustand>
                Das Terminal holt sich in den nächsten Sekunden seine Verbindung und wechselt von
                selbst zur Anmeldung.
              </p>
              <div className="flex flex-wrap gap-2">
                {stationId && (
                  <Link to={`/admin/stations/${stationId}`} className={KNOPF_LEISE}>
                    Zur Station
                  </Link>
                )}
                <button
                  onClick={() => {
                    setSchritt('code');
                    setCode('');
                    setName('');
                    setStandort('');
                  }}
                  className={KNOPF_LEISE}
                >
                  Weiteres Gerät koppeln
                </button>
              </div>
            </div>
          </Karte>
        )}

        <Notiz titel="Warum so herum?">
          Die Genehmigung lebt in deiner angemeldeten Sitzung (mit Zwei-Faktor) — der Kopplungs-Code
          existiert nur auf dem Terminal-Bildschirm und in deinem Scan, nie als Link oder Text zum
          Kopieren. Ein Gerät wird nur dann zur Station, wenn jemand mit Admin-Rechten physisch
          davorsteht.
        </Notiz>
      </div>
    </LeitstandRahmen>
  );
}
